/* eslint-disable no-param-reassign */
import {
  useState, useEffect, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  useKpiModifiers,
  useModifierConfig,
} from '../../../hooks/modifiers';
import KpiFilterApplied from '../../KpiFilterApplied';
import { GROUP_MAP } from '../../../utils/models/customColumnsOperations';
import { verifyColumnName } from '../../../juristec-ui/utils/validators/inputTextValidators';

import StepProgress from '../../../juristec-ui/core/StepProgress';
import { PreviewStep, MapStep } from './Steps';
import { GroupsContainer, LabelSmall, EmptyMsg } from './Steps/styled/MapStep.styled';

const getDfTypes = (columns, custom, editCustom) => {
  let vColumns = {};
  if (columns) {
    const removed = [];
    const fromCustom = custom.reduce((aux, cc) => {
      if (editCustom?.name === cc.name) return aux;
      if (cc.operator === 'REMOVE') removed.push(cc.name);
      else aux[cc.name] = cc.operator.type;
      return aux;
    }, {});
    vColumns = Object.entries(columns).reduce((aux, [k, v]) => {
      if (removed.includes(k)) return aux;
      if (editCustom?.name === k) return aux;
      if (k in fromCustom) return aux;
      aux[k] = v;
      return aux;
    }, fromCustom);
    return vColumns;
  }
  return vColumns;
};

const manageCustomColumnsDefaultPropColumns = {};
const manageCustomColumnsDefaultPropCustom = [];

const ManageCustomColumns = ({
  columns = manageCustomColumnsDefaultPropColumns,
  custom = manageCustomColumnsDefaultPropCustom,
  editCustom = null,
  genPreview,
  getUnique,
  submit,
  hide,
}) => {
  const isEditSet = useRef(false);
  const validColumns = useMemo(() => getDfTypes(columns, custom, editCustom));
  const [previewCheck, setPreviewCheck] = useState(false);
  const [columnName, setColumnName] = useState({ value: '', error: true, errorMsg: '' });

  const {
    modifierConfig,
    simpleOptions,
    getColumnOptions,
    getSimpleOptions,
    handleModifierConfigs,
    resetModifierConfigs,
    //
    configColumnIsDate,
    getOptionsByConfigGroup,
    checkModifierValues,
  } = useModifierConfig({
    dataFrameTypes: validColumns,
    modifierOrigins: { group: 'Agrupamento' },
    getUniqueValues: getUnique,
  });

  const {
    modifiers,
    addModifier,
    removeModifier,
    editModifier,
    changeModifierOrder,
  } = useKpiModifiers({
    dataFrameTypes: validColumns,
    initialModifiers: { group: editCustom?.inputs ?? [] },
  });

  useEffect(() => {
    if (editCustom && !isEditSet.current) {
      const colName = editCustom.inputs[0].column;
      handleModifierConfigs({
        column: {
          label: colName, value: colName, type: validColumns[colName],
        },
        origin: {
          label: 'Agrupamento', value: 'group', id: 'group',
        },
      });
      setColumnName({ value: editCustom.name, error: false, errorMsg: '' });
      isEditSet.current = true;
    }
  }, []);

  const handleNewName = (value) => {
    const msg = verifyColumnName(value, validColumns);
    setColumnName({
      value,
      error: msg.length !== 0,
      errorMsg: msg,
    });
  };

  const checkMap = () => (
    modifiers.group?.length > 0
    && !columnName.error
    && columnName.value.length > 0
  );

  const handleAddModifier = () => {
    addModifier(modifierConfig);
    resetModifierConfigs(['column', 'origin']);
  };

  const mapStep = {
    label: 'Criação',
    validation: checkMap,
    disableFocus: true,
    content: (
      <MapStep
        modifiers={modifiers}
        validColumns={validColumns}
        getUnique={getUnique}
        editMode={Boolean(editCustom)}
        hasMap={modifiers.group.length > 0}
        columnNameState={[columnName, handleNewName]}
        columnOpts={Object.entries(validColumns).map(([k, v]) => ({
          label: k, value: k, id: k, type: v,
        }))}
        modifierConfig={modifierConfig}
        simpleOptions={simpleOptions}
        getColumnOptions={getColumnOptions}
        getSimpleOptions={getSimpleOptions}
        handleModifierConfigs={handleModifierConfigs}
        resetModifierConfigs={resetModifierConfigs}
        configColumnIsDate={configColumnIsDate}
        getOptionsByConfigGroup={getOptionsByConfigGroup}
        checkModifierValues={checkModifierValues}
        addNewGroup={handleAddModifier}
      >
        {modifiers.group?.length > 0 ? (
          <GroupsContainer>
            <LabelSmall>
              Deparas Aplicados
            </LabelSmall>
            {modifiers.group?.map((m, i) => (
              <KpiFilterApplied
                nameEdit
                key={m.uid}
                origin="group"
                // @ts-expect-error not typed yet
                modifier={m}
                modifiers={modifiers}
                removeFunc={removeModifier}
                editFunc={editModifier}
                getOptions={getUnique}
                dataFrameTypes={validColumns}
                position={i}
                isLast={modifiers.group.length - 1 === i}
                changePosition={changeModifierOrder}
                atModal
              />
            ))}
          </GroupsContainer>
        ) : (
          <EmptyMsg>
            <span>Nenhum depara selecionado</span>
          </EmptyMsg>
        )}
      </MapStep>
    ),
  };

  const checkPreview = () => previewCheck;

  const previewStep = {
    label: 'Prévia',
    validation: checkPreview,
    content: (
      <PreviewStep
        custom={custom}
        newCColumn={{
          inputs: modifiers.group,
          operator: GROUP_MAP,
          name: columnName?.value ?? '',
        }}
        genPreview={genPreview}
        previewCheck={setPreviewCheck}
      />
    ),
  };

  const handleSubmit = () => {
    submit({
      inputs: [...modifiers.group],
      operator: GROUP_MAP,
      name: columnName?.value ?? '',
    });
    hide();
  };

  return (
    <>
      <StepProgress
        steps={[mapStep, previewStep]}
        hide={hide}
        complete={handleSubmit}
        markersWidth="large"
        disableUntilValid
      />
    </>
  );
};

ManageCustomColumns.propTypes = {
  columns: PropTypes.objectOf(PropTypes.string),
  custom: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    operator: PropTypes.oneOfType([
      PropTypes.shape({
        value: PropTypes.string,
      }),
      PropTypes.string,
    ]),
    inputs: PropTypes.arrayOf(PropTypes.shape({})),
  })),
  editCustom: PropTypes.shape({
    name: PropTypes.string,
    operator: PropTypes.shape({
      type: PropTypes.string,
      maxInputs: PropTypes.number,
      value: PropTypes.string,
    }),
    inputs: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  genPreview: PropTypes.func.isRequired,
  getUnique: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  hide: PropTypes.func.isRequired,
};

export default ManageCustomColumns;
